import { getOnlyTLD } from "helpers/helper";

// Define all possible free domain extensions
const ALL_FREE_EXTENSIONS = [
  ".com",
  ".web.id",
  ".or.id",
  ".sch.id",
  ".biz.id",
  ".ac.id",
  ".ponpes.id",
  ".my.id"
];

// Define PID-specific free domain configurations
export const FREE_DOMAIN_CONFIGS = {
  // PIDs that only allow .my.id and .biz.id
  myidBizidOnly: {
    pids: [1124, 1125],
    extensions: [".my.id", ".biz.id"]
  },
  // PIDs that allow com and some id extensions
  comAndSomeId: {
    pids: [1126, 1127, 1115],
    extensions: [".com", ".my.id", ".or.id", ".web.id", ".biz.id", ".co.id"]
  },
  // PIDs that allow some id extensions
  someIdExtensions: {
    pids: [1128, 1129, 1130, 1131],
    extensions: [".my.id", ".web.id", ".or.id", ".biz.id"]
  },
  // PIDs that allow com and some id extensions
  comAndSomeId2: {
    pids: [1132, 1133, 1134, 1135],
    extensions: [".com", ".my.id", ".or.id", ".web.id", ".biz.id", ".co.id"]
  },
  // PIDs that only allow .my.id
  myidOnly: {
    pids: [1010, 1011, 1006, 1007],
    extensions: [".my.id"]
  },
  // PIDs that allow all extensions
  allExtensions: {
    pids: [1013, 1014, 1107, 1108, 1109, 1008, 1009],
    extensions: ALL_FREE_EXTENSIONS
  },
  // PIDs that allow premium extensions (1136-1143)
  premiumExtensions: {
    pids: [1136, 1137, 1138, 1139, 1140, 1141, 1142, 1143],
    extensions: [".com", ".net", ".org", ".id", ".co.id", ".my.id", ".or.id", ".web.id"]
  }
};

// Helper function untuk check free domain
export const checkFreeDomain = (domain, duration, extList) => {
  return extList.includes(getOnlyTLD(domain)) && Number(duration) === 1;
};

// Helper function untuk check hosting di cart
export const hasHostingInCart = (cartItems) => {
  if (cartItems.length > 0) {
    const hasHosting = cartItems.some(item => item.type === "hostingaccount");
    return hasHosting;
  }
  return false;
};

// Helper function untuk check hosting dengan durasi minimal 1 tahun
export const hasValidHostingDuration = (cartItems) => {
  if (cartItems.length > 0) {
    const validBillingCycles = ["annually", "biennially", "triennially"];
    return cartItems.some(item =>
      item.type === "hostingaccount" &&
      validBillingCycles.includes(item.billingcycle)
    );
  }
  return false;
};

// Helper function untuk check hosting Flex WP di cart
export const hasFlexWPInCart = (cartItems) => {
  return cartItems.some(item =>
    item.pid === 1117 && item.billingcycle === "annually"
  );
};

// Helper untuk cek apakah sudah ada domain gratis di cart (register OR transfer)
export const hasFreeDomainInCart = (cartItems) => {
  return cartItems.some(
    (item) => (item.type === "register" || item.type === "transfer") &&
      item.price === 0 &&
      item.duration === "1"
  );
};

// Function untuk menentukan apakah domain gratis
export const getIsFreeDomain = (domain, duration, cartItems, isTransfer = false) => {
  // Cek apakah ada Flex WP annually di cart
  if (hasFlexWPInCart(cartItems)) {
    return false;
  }

  // Jika transfer, cek dulu apakah ada hosting di cart
  if (isTransfer && !hasHostingInCart(cartItems)) {
    return false;
  }

  // Cek apakah domain durasi 1 tahun dan hosting minimal 1 tahun
  if (Number(duration) !== 1 || !hasValidHostingDuration(cartItems)) {
    return false;
  }

  // Get hosting product from cart
  const hostingProduct = cartItems.find(item => item.type === "hostingaccount");
  if (!hostingProduct) {
    return false;
  }

  const hostingPid = hostingProduct.pid;

  // Get allowed extensions based on PID
  let allowedExtensions = [];

  if (FREE_DOMAIN_CONFIGS.myidBizidOnly.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.myidBizidOnly.extensions;
  } else if (FREE_DOMAIN_CONFIGS.comAndSomeId.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId.extensions;
  } else if (FREE_DOMAIN_CONFIGS.someIdExtensions.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.someIdExtensions.extensions;
  } else if (FREE_DOMAIN_CONFIGS.comAndSomeId2.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.comAndSomeId2.extensions;
  } else if (FREE_DOMAIN_CONFIGS.myidOnly.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.myidOnly.extensions;
  } else if (FREE_DOMAIN_CONFIGS.allExtensions.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.allExtensions.extensions;
  } else if (FREE_DOMAIN_CONFIGS.premiumExtensions.pids.includes(hostingPid)) {
    allowedExtensions = FREE_DOMAIN_CONFIGS.premiumExtensions.extensions;
  } else {
    return false;
  }

  // Check if the domain extension is allowed for this PID
  const domainExtension = getOnlyTLD(domain);
  if (!allowedExtensions.includes(domainExtension)) {
    return false;
  }

  return true;
};
